import { useOutletContext, useParams } from "react-router-dom";
import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import MetricGrid from "@/components/MetricGrid";
import Table, { TableHeading, TableFooter } from "@/components/Table";
// import Button from "@/components/Button";
import EmptyState from "@/components/EmptyState";
import { buildColumns } from "./_utils";
import { Student } from "../../types";
import { StudentTestResult } from "./types";
import { buildStudentTestPassMetrics } from "./studentTestPassMetrics";
import { GET_UNIVERSITY_STUDENTS_WITH_TEST_SCORES } from "../../../test-results/queries";
import { usePersonEducationCapability } from "@/pages/people/[pgaId]/person/_usePersonEducationAccess";
import { Navigate } from "react-router-dom";

export default function StudentTestResultsPage() {
  const { universityId, studentId } = useParams();
  const { student } = useOutletContext<{ student: Student }>();
  const { loaded, canViewPersonEducation } = usePersonEducationCapability();

  const { data, loading } = useQuery(GET_UNIVERSITY_STUDENTS_WITH_TEST_SCORES, {
    variables: {
      input: {
        universityLegacyIds: [universityId?.toString() || ""],
        alumni: false,
        search: student.pgaId
      }
    },
    skip: !loaded || !canViewPersonEducation || !universityId || !student.pgaId
  });

  const testResultsData: StudentTestResult[] = useMemo(() => {
    if (!data?.universityStudents?.nodes) return [];

    const allTestResults: StudentTestResult[] = [];

    for (const studentData of data.universityStudents.nodes) {
      const testHistory = studentData.educationTestHistory || [];

      for (const testResult of testHistory) {
        allTestResults.push({
          id: `${studentData.id}-${testResult.testId}-${testResult.attemptNumber}`,
          testId: testResult.testId,
          testName: testResult.testDefinition?.description || testResult.testId,
          level: testResult.levelCode,
          testDate: testResult.testDate,
          isRetake: testResult.isRetake,
          score: testResult.score,
          maxScore: testResult.maxScore,
          passed: testResult.isPassed,
          oldTestId: testResult.testDefinition?.oldTestId,
          student: {
            id: studentData.id,
            firstName: studentData.firstName,
            lastName: studentData.lastName,
            pgaId: studentData.pgaId,
            email: ""
          }
        });
      }
    }

    return allTestResults;
  }, [data]);

  const testResultsMetrics = useMemo(() => buildStudentTestPassMetrics(testResultsData), [testResultsData]);

  const columns = useMemo(() => buildColumns(), []);

  if (!loaded || !canViewPersonEducation) {
    if (loaded && !canViewPersonEducation && universityId && studentId) {
      return <Navigate to={`/education/universities/${universityId}/students/${studentId}/pat-results`} replace />;
    }
    return null;
  }

  return (
    <>
      {testResultsMetrics.length > 0 && <MetricGrid metrics={testResultsMetrics} layout="expanded" />}

      <TableHeading
        heading="PGM Program Test Results"
        body="View all test results associated with this student."
        actions={[
          // {
          //   label: "Export Test Results",
          //   icon: "download" as const,
          //   variant: "text" as const,
          //   onClick: () => {},
          //   testId: "export-test-results-button"
          // }
        ]}
      />
      <Table
        data={testResultsData}
        columns={columns}
        loading={loading}
        renderNoDataEmptyState={() => (
          <EmptyState title="No Test Results" caption="There are no records to display at this time" iconName="quiz" />
        )}
        renderFooter={() => <TableFooter totalRowCount={testResultsData.length} />}
      />
    </>
  );
}
